// ─────────────────────────────────────────────────────────────────────────────
// Crew — privacy & card-link settings (Agent B). Part 4 of the funnel.
//
// CONSENT: granular + revocable per UK-GDPR. `essential` is always-on and shown
// as a non-toggle row (the service can't run without it). analytics / marketing /
// card_link are real toggles → api.setConsent(scope, granted). After ANY change we
// call refreshConsentSnapshot() so the analytics SDK re-reads the authoritative set
// before the next event.
//
// CARD-LINK GUARDRAIL (copy lives here, in full): linking attaches your EXISTING
// card to a third-party provider for attribution / card-linked rewards only. Crew
// NEVER issues a card, and the pot is tracking-only — it never holds or moves money.
// The control is consent-gated: linking without card_link consent returns 403, and
// we surface a clear "grant card_link first" prompt rather than a raw error.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import type { CardLink, Consent, ConsentScope } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { refreshConsentSnapshot } from '../lib/analytics';
import { consentMap, ALWAYS_ON_SCOPES } from '../lib/consent';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { ErrorBanner, Spinner } from '../components/Feedback';
import { errorMessage } from '../lib/format';

// Optional scopes the user can actually toggle (essential is rendered separately).
const TOGGLEABLE: { scope: Exclude<ConsentScope, 'essential'>; label: string; help: string }[] = [
  {
    scope: 'analytics',
    label: 'Product analytics',
    help: 'Lets us measure how crews plan together so we can improve Crew. Off by default.',
  },
  {
    scope: 'marketing',
    label: 'Marketing messages',
    help: 'Occasional emails about new features and ideas for your crew. You can opt out any time.',
  },
  {
    scope: 'card_link',
    label: 'Card-linked offers',
    help: 'Allows you to link your existing card for attribution and card-linked rewards (see below).',
  },
];

const SCOPE_LABEL: Record<ConsentScope, string> = {
  essential: 'Essential',
  analytics: 'Product analytics',
  card_link: 'Card-linked offers',
  marketing: 'Marketing messages',
};

interface SettingsProps {
  onSignOut: () => void;
}

export function Settings({ onSignOut }: SettingsProps) {
  const [consents, setConsents] = useState<Consent[] | null>(null);
  const [cardLink, setCardLink] = useState<CardLink | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  // Per-scope busy flag so each toggle shows its own pending state.
  const [busyScope, setBusyScope] = useState<ConsentScope | null>(null);
  const [cardBusy, setCardBusy] = useState(false);

  // ── Load consent + card-link state together ─────────────────────────────────
  useEffect(() => {
    let alive = true;
    setConsents(null);
    setCardLink(null);
    setLoadError(null);
    Promise.all([api.getConsent(), api.getCardLink()])
      .then(([c, cl]) => {
        if (!alive) return;
        setConsents(c.consents);
        setCardLink(cl.cardLink);
      })
      .catch((err: unknown) => alive && setLoadError(errorMessage(err, 'Could not load your settings. Please try again.')));
    return () => {
      alive = false;
    };
  }, [retryKey]);

  const map = consents ? consentMap(consents) : null;
  const cardLinkAllowed = map?.card_link === true;
  const cardLinked = cardLink?.active === true;

  async function toggle(scope: Exclude<ConsentScope, 'essential'>, granted: boolean) {
    setBusyScope(scope);
    setActionError(null);
    try {
      const res = await api.setConsent(scope, granted);
      setConsents(res.consents);
      // Invalidate the SDK's cached snapshot so the next event re-reads consent.
      refreshConsentSnapshot();
      // Revoking card_link consent should also tear down any active card link so the
      // two never drift; the server enforces this too, but we reflect it locally.
      if (scope === 'card_link' && !granted && cardLink?.active) {
        try {
          await api.unlinkCard();
          setCardLink(null);
        } catch {
          // Re-read on next load if the server kept the link; non-fatal here.
        }
      }
    } catch (err) {
      setActionError(errorMessage(err, 'Could not update that preference. Please try again.'));
    } finally {
      setBusyScope(null);
    }
  }

  async function linkCard() {
    setCardBusy(true);
    setActionError(null);
    try {
      const res = await api.linkCard();
      setCardLink(res.cardLink);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('Turn on “Card-linked offers” above first — linking a card needs that consent.');
      } else {
        setActionError(errorMessage(err, 'Could not link your card. Please try again.'));
      }
    } finally {
      setCardBusy(false);
    }
  }

  async function unlinkCard() {
    setCardBusy(true);
    setActionError(null);
    try {
      await api.unlinkCard();
      setCardLink(null);
    } catch (err) {
      setActionError(errorMessage(err, 'Could not unlink your card. Please try again.'));
    } finally {
      setCardBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">Settings</h1>
        <p className="mt-1 text-sm text-ink-700/70">
          Manage your privacy choices and card-linked offers. Everything here is optional.
        </p>
      </div>

      {actionError && <ErrorBanner message={actionError} />}

      {loadError && <ErrorBanner message={loadError} onRetry={() => setRetryKey((k) => k + 1)} />}

      {consents === null && !loadError && <Spinner label="Loading your settings…" />}

      {map && (
        <>
          {/* ── Consent ──────────────────────────────────────────────────────── */}
          <Card>
            <CardTitle>Privacy choices</CardTitle>
            <p className="mt-1 text-sm text-ink-700/70">
              Granular and revocable. Change any of these whenever you like.
            </p>

            <ul className="mt-4 flex flex-col divide-y divide-sand-200">
              {/* Essential — always on, not a toggle */}
              <li className="flex items-start justify-between gap-4 py-3">
                <div>
                  <p className="text-sm font-medium text-ink-800">{SCOPE_LABEL.essential}</p>
                  <p className="mt-0.5 text-xs text-ink-700/70">
                    Keeps you signed in and the app working. Always on — the service can&rsquo;t run without it.
                  </p>
                </div>
                <span className="mt-0.5 shrink-0 rounded-full bg-sand-100 px-3 py-1 text-xs font-semibold text-ink-700">
                  Always on
                </span>
              </li>

              {TOGGLEABLE.map(({ scope, label, help }) => {
                const on = map[scope];
                const busy = busyScope === scope;
                return (
                  <li key={scope} className="flex items-start justify-between gap-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-ink-800">{label}</p>
                      <p className="mt-0.5 text-xs text-ink-700/70">{help}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`${label}: ${on ? 'on' : 'off'}`}
                      disabled={busy || ALWAYS_ON_SCOPES.has(scope)}
                      onClick={() => toggle(scope, !on)}
                      className={
                        'relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ' +
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-500 focus-visible:ring-offset-2 ' +
                        'focus-visible:ring-offset-white disabled:opacity-50 ' +
                        (on ? 'bg-coral-500' : 'bg-sand-200')
                      }
                    >
                      <span
                        aria-hidden
                        className={
                          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition ' +
                          (on ? 'translate-x-6' : 'translate-x-1')
                        }
                      />
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="mt-4 text-xs text-ink-700/70">
              Read the full{' '}
              <a
                href="#/privacy"
                className="font-medium underline underline-offset-2 hover:text-ink-900"
              >
                privacy &amp; data rights
              </a>{' '}
              notice.
            </p>
          </Card>

          {/* ── Card-link readiness (consent-gated) ──────────────────────────── */}
          <Card>
            <CardTitle>Card-linked offers</CardTitle>
            <div className="mt-2 space-y-2 text-sm text-ink-700">
              <p>
                Linking attaches your <span className="font-medium">existing</span> bank card to a
                third-party provider so eligible card-linked rewards can be matched to your spend.
              </p>
              <p className="rounded-xl2 bg-sand-50/80 p-3 text-xs text-ink-700/80 ring-1 ring-sand-200">
                <span className="font-semibold text-ink-800">Crew never issues a card</span> and never
                sees your full card number. The crew pot is{' '}
                <span className="font-semibold text-ink-800">tracking-only</span> — it never holds or
                moves money. You can unlink at any time.
              </p>
            </div>

            <div className="mt-4">
              {!cardLinkAllowed ? (
                <p className="rounded-xl2 bg-amber-400/15 px-4 py-3 text-sm text-amber-500 ring-1 ring-amber-400/40">
                  Turn on <span className="font-semibold">Card-linked offers</span> in Privacy choices
                  above to enable card linking.
                </p>
              ) : cardLinked ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium text-ink-800">
                    Card linked
                    {cardLink?.provider ? ` via ${cardLink.provider}` : ''}. You&rsquo;re ready for
                    card-linked rewards.
                  </p>
                  <Button variant="danger" busy={cardBusy} onClick={unlinkCard}>
                    Unlink card
                  </Button>
                </div>
              ) : (
                <Button busy={cardBusy} onClick={linkCard}>
                  Link my existing card
                </Button>
              )}
            </div>
          </Card>
        </>
      )}

      {/* ── Account ──────────────────────────────────────────────────────────── */}
      <Card>
        <CardTitle>Account</CardTitle>
        <p className="mt-1 text-sm text-ink-700/70">Signed in with a magic link.</p>
        <div className="mt-4">
          <Button variant="secondary" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
